<div class="w-full flex justify-center">
  <div class="max-w-[1800px] w-full">
    <zaeper-page-layout [pageLayout]="ePageLayout.CARDS" [title]="'Receiver Dashboard'" [navigation]="navigation">
  <ng-template zTemplate="header">
    <zaeper-icon fontSet="material-icons-outlined">tv</zaeper-icon>
  </ng-template>

  <div class="flex flex-col gap-6">
    <!-- Action Buttons Row -->
    <div class="flex flex-row justify-start items-center gap-2">
      @if (!(activeReceiver$ | async)) {
        <p-button (onClick)="openConnectReceiverModal()" [rounded]="true"
                  icon="pi pi-plus" label="Receiver Verbinden"/>
      }
    </div>

    <!-- Dashboard Content -->
    <zaeper-card>
      <div class="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <!-- Left Column: Preview + Statistics -->
        <div class="xl:col-span-2 flex flex-col gap-4">
          <!-- Preview Screen Card -->
          <p-card styleClass="shadow-sm">
            <ng-template pTemplate="header">
              <div class="flex flex-row justify-between items-center px-4 pt-4">
                <div class="flex flex-row items-center gap-3">
                  <h3 class="text-lg font-semibold m-0">Bildschirmvorschau</h3>
                  @if (!(activeReceiver$ | async)) {
                    <p-tag value="Kein Receiver verbunden"/>
                  }
                </div>
                <app-online-status-indicator [isOnline]="(isReceiverOnline$ | async) ?? false"/>
              </div>
            </ng-template>
            <div class="flex justify-center items-center p-2">
              <div #previewerFrameWrapper class="flex flex-col max-w-full justify-center max-h-[50vh] w-full aspect-video">
                <div #previewerFrame class="relative max-w-full w-full h-full p-1.5 rounded-xl bg-gradient-to-br from-gray-950 to-gray-500">
                  <div class="h-full w-full rounded-lg overflow-hidden bg-gradient-to-br from-gray-900 to-gray-700">
                    @if (activeReceiver$ | async) {
                      <div class="contents" [ngClass]="{'hidden': (receiverViewMode$ | async) !== eReceiverViewMode.PREVIEW}">
                        <ng-container #previewContainer></ng-container>
                      </div>
                      <div class="contents" [ngClass]="{'hidden': (receiverViewMode$ | async) !== eReceiverViewMode.LIVE_VIEW}">
                        <ng-container #liveViewContainer></ng-container>
                      </div>
                    } @else {
                      <app-receiver-placeholder/>
                    }
                  </div>
                </div>
              </div>
            </div>
            <div class="flex justify-between items-center pl-2 pt-4">
              <zaeper-toggle-button [buttons]="toggleButtons" [disabled]="!(activeReceiver$ | async)" (onChange)="switchViewMode($event)"/>
              <div class="flex flex-row gap-2">
                <p-button [rounded]="true" [outlined]="true" icon="pi pi-sync" pTooltip="Synchronisieren" tooltipPosition="top" [disabled]="!(activeReceiver$ | async)"/>
                <p-button [rounded]="true" [outlined]="true" icon="pi pi-calendar" pTooltip="Geplante Synchronisierungen" tooltipPosition="top" [disabled]="!(activeReceiver$ | async)"/>
                <p-button [rounded]="true" [outlined]="true" icon="pi pi-expand" pTooltip="Vollbild" tooltipPosition="top" (onClick)="openFullscreenPreview()" [disabled]="!(activeReceiver$ | async)"/>
                <p-button [rounded]="true" icon="pi pi-cog" pTooltip="Einstellungen" tooltipPosition="top" (onClick)="openApplicationsOverviewModal()" [disabled]="!(activeReceiver$ | async)"/>
              </div>
            </div>
          </p-card>

          <!-- Receiver Statistics Cards (2x4 Grid) -->
          <zaeper-card-group styleClass="grid grid-cols-2 md:grid-cols-4 gap-4">
            <app-statistic-card
              icon="pi pi-microchip"
              iconColor="text-primary"
              [value]="(activeReceiver$ | async) ? cpuUsage + '%' : '--'"
              label="CPU Auslastung"/>
            <app-statistic-card
              icon="pi pi-server"
              iconColor="text-primary"
              [value]="(activeReceiver$ | async) ? memoryUsage + '%' : '--'"
              label="RAM Auslastung"/>
            <app-statistic-card
              icon="pi pi-database"
              iconColor="text-primary"
              [value]="(activeReceiver$ | async) ? storageUsage + '%' : '--'"
              label="Speicher"/>
            <app-statistic-card
              icon="pi pi-wifi"
              iconColor="text-primary"
              [value]="(activeReceiver$ | async) ? networkStatus : '--'"
              label="Netzwerk"/>
            <app-statistic-card
              icon="pi pi-th-large"
              iconColor="text-primary"
              [value]="(activeReceiver$ | async) ? '' + installedApps : '--'"
              label="Installierte Apps"/>
            <app-statistic-card
              icon="pi pi-calendar"
              iconColor="text-primary"
              [value]="(activeReceiver$ | async) ? '' + scheduledTasks : '--'"
              label="Geplante Tasks"/>
            <app-statistic-card
              icon="pi pi-exclamation-triangle"
              iconColor="text-primary"
              [value]="(activeReceiver$ | async) ? '' + warnings : '--'"
              label="Warnungen"/>
            <app-statistic-card
              icon="pi pi-check-circle"
              iconColor="text-primary"
              [value]="(activeReceiver$ | async) ? healthStatus : '--'"
              label="Systemstatus"/>
          </zaeper-card-group>
        </div>

        <!-- Right Column: Statistics + Receivers List -->
        <div class="flex flex-col gap-4 h-full">
          <!-- Overall Statistics Cards (1x2 Grid) -->
          <zaeper-card-group styleClass="grid grid-cols-2 gap-4">
            <app-statistic-card
              icon="pi pi-clock"
              iconColor="text-zaeper-primary-500"
              [value]="(activeReceiver$ | async) ? uptime : '--'"
              label="Betriebszeit"
              valueSize="large"/>
            <app-statistic-card
              icon="pi pi-refresh"
              iconColor="text-zaeper-primary-500"
              [value]="(activeReceiver$ | async) ? lastSync : '--'"
              label="Letzte Sync"
              valueSize="large"/>
          </zaeper-card-group>

          <!-- Connected Receivers List -->
          <p-card styleClass="shadow-sm flex-1">
            <ng-template pTemplate="header">
              <div class="flex flex-row justify-between items-center px-4 pt-4">
                <h3 class="text-lg font-semibold m-0">Verbundene Receiver <span class="font-normal text-surface-400 dark:text-surface-500">({{ (activeGroup$ | async)?.name }})</span></h3>
                <p-button [rounded]="true" [outlined]="true" icon="pi pi-sync" pTooltip="Gruppe synchronisieren" tooltipPosition="top" [disabled]="((receivers$ | async)?.length ?? 0) <= 1"/>
              </div>
            </ng-template>
            <div class="overflow-y-auto">
              @for (receiver of (receivers$ | async); track receiver.id) {
                <app-receiver-list-item
                  [receiver]="receiver"
                  [isActive]="receiver.id === (activeReceiver$ | async)?.id"
                  (onSelect)="switchToReceiver($event)"/>
              } @empty {
                <div class="text-surface-500 dark:text-surface-400 p-4">Keine Receiver in dieser Gruppe</div>
              }
            </div>
          </p-card>
        </div>
      </div>
    </zaeper-card>
  </div>
</zaeper-page-layout>
  </div>
</div>
